import ClearbitIcon from '@clearkit/icons/glyphs/Clearbit';
import HubspotIcon from '@clearkit/icons/glyphs/Hubspot';
import SalesforceIcon from '@clearkit/icons/glyphs/Salesforce';
import TraitIcon from '@clearkit/icons/glyphs/Trait';
import { Link } from 'react-router-dom';

import HubspotObjectLink from '~/components/Hubspot/ObjectLink';
import LoadingArea from '~/components/LoadingArea';
import SalesforceObjectLink from '~/components/Salesforce/ObjectLink';
import { SidebarContent } from '~/components/Sidebar';
import KeyValue from '~/components/Sidebar/KeyValue';
import TraitsTable from '~/components/Sidebar/TraitsTable';
import { formatHubspotProperties } from '~/lib/hubspot';
import paths from '~/lib/paths';

import styles from './Details.style';

class Details extends React.Component {
  companyLink = () => {
    const { person } = this.props;
    const { company } = person;
    const { company_id, company_domain } = person;

    if (company_id && company.name) {
      return <Link to={paths.company(person.company_id)}>{company.name}</Link>;
    } else if (company.name) {
      return company.name;
    } else if (company_domain) {
      return company_domain;
    } else {
      return null;
    }
  };

  render() {
    const { classes, person } = this.props;
    const { company } = person;

    return (
      <SidebarContent noPadding>
        <div className={classnames(classes.enrichment, this.props.className)}>
          <KeyValue
            data={[
              {
                label: 'Email',
                value: person.email,
                link: `mailto:${person.email}`,
              },
              { label: 'Bio', value: person.bio },
              { label: 'Employment', value: person.employment },
            ]}
            social={{
              twitter: person.twitter,
              facebook: person.facebook,
              github: person.github,
              linkedin: person.linkedin,
            }}
          />

          {company.domain && (
            <LoadingArea
              loading={!person.loaded}
              className={classes.companyLoading}>
              <KeyValue
                data={[
                  { label: 'Company Name', value: this.companyLink() },
                  { label: 'Company Description', value: company.description },
                  {
                    label: 'Company Revenue (est)',
                    value: company.revenue,
                  },
                  { label: 'Company Size', value: company.size },
                  { label: 'Company Location', value: company.location },
                ]}
                social={{
                  twitter: company.twitter,
                  facebook: company.facebook,
                  github: company.github,
                  linkedin: company.linkedin,
                  website: company.domain,
                }}
              />
            </LoadingArea>
          )}
        </div>
        <div className={classnames(this.props.className, classes.traitsTable)}>
          <TraitsTable traits={traitsForPerson(person)} />
        </div>
      </SidebarContent>
    );
  }
}

function traitsForPerson(person) {
  if (!person) return;

  let salesforceContacts = [];
  let salesforceLeads = [];
  let salesforceAccounts = [];

  let hubspotContacts = [];
  let hubspotCompanies = [];
  let hubspotDeals = [];

  if (person.salesforce) {
    salesforceContacts = person.salesforce.contacts.map((contact) => ({
      sectionAdornment: <SalesforceObjectLink id={contact['Contact ID']} />,
      sectionTitle: (
        <>
          <SalesforceIcon height={16} width={16} /> Contact:{' '}
          {contact['Full Name']}
        </>
      ),
      attributes: contact,
    }));

    salesforceLeads = person.salesforce.leads.map((lead) => ({
      sectionAdornment: <SalesforceObjectLink id={lead['Lead ID']} />,
      sectionTitle: (
        <>
          <SalesforceIcon height={16} width={16} /> Lead: {lead['Full Name']}
        </>
      ),
      attributes: lead,
    }));

    salesforceAccounts = person.salesforce.accounts.map((account) => ({
      sectionAdornment: <SalesforceObjectLink id={account['Account ID']} />,
      sectionTitle: (
        <>
          <SalesforceIcon height={16} width={16} /> Account:{' '}
          {account['Account Name']}
        </>
      ),
      attributes: account,
    }));
  }

  if (person.hubspot) {
    hubspotCompanies = person.hubspot.companies.map((company) => ({
      sectionAdornment: (
        <HubspotObjectLink
          path={`contacts/${company.hubspot_account_id}/company/${company.id}`}
        />
      ),
      sectionTitle: (
        <>
          <HubspotIcon height={16} width={16} /> Company:{' '}
          {company.properties.name}
        </>
      ),
      attributes: formatHubspotProperties(company.properties),
    }));

    hubspotContacts = person.hubspot.contacts.map((contact) => ({
      sectionAdornment: (
        <HubspotObjectLink
          path={`contacts/${contact.hubspot_account_id}/contact/${contact.id}`}
        />
      ),
      sectionTitle: (
        <>
          <HubspotIcon height={16} width={16} /> Contact:{' '}
          {contact.properties.firstname}
        </>
      ),
      attributes: formatHubspotProperties(contact.properties),
    }));

    hubspotDeals = person.hubspot.deals.map((deal) => ({
      sectionAdornment: (
        <HubspotObjectLink
          path={`contacts/${deal.hubspot_account_id}/deal/${deal.id}`}
        />
      ),
      sectionTitle: (
        <>
          <HubspotIcon height={16} width={16} /> Deal: {deal.name}
        </>
      ),
      attributes: formatHubspotProperties(deal.properties),
    }));
  }

  return [
    {
      sectionTitle: (
        <>
          <ClearbitIcon width={16} height={16} /> Enrichment
        </>
      ),
      attributes: person.enrichment,
    },
    {
      sectionTitle: (
        <>
          <TraitIcon width={16} height={16} /> Custom Traits
        </>
      ),
      attributes: person.traits,
    },
    ...salesforceContacts,
    ...salesforceLeads,
    ...salesforceAccounts,
    ...hubspotCompanies,
    ...hubspotContacts,
    ...hubspotDeals,
  ];
}

Details.propTypes = {
  classes: PropTypes.object,
  person: PropTypes.shape({
    id: PropTypes.string,
  }).isRequired,
  className: PropTypes.string,
};

export default withStyles(styles)(Details);
